import { Request, Response, Router } from "express";

import { profileController } from "../controllers/profile-controller";
import {
  authenticate,
  createRateLimit,
  requireEmailVerification,
} from "../middleware/auth-middleware";
import {
  uploadPersonalFiles,
  uploadSchoolFiles,
  uploadStudentFiles,
  uploadTeacherFiles,
} from "../middleware/upload-middleware";

const router = Router();

// Environment-aware rate limiting
const generalRateLimit = createRateLimit.general(); // 50 req/5min (prod) | unlimited (dev)
const uploadRateLimit = createRateLimit.upload(); // 20 req/10min (prod) | unlimited (dev)

/**
 * @route   POST /api/v1/profile/personal
 * @desc    Create personal profile (for school users)
 * @access  Private (requires authentication and email verification)
 * @body    FormData with fullName, gender, dateOfBirth, phone, address, bio, profileImage
 */
router.post(
  "/personal",
  authenticate,
  requireEmailVerification,
  uploadRateLimit,
  uploadPersonalFiles,
  (req: Request, res: Response) =>
    profileController.createPersonalProfile(req, res),
);

/**
 * @route   POST /api/v1/profile/school
 * @desc    Create school profile
 * @access  Private (requires authentication and email verification)
 * @body    FormData with schoolName, establishmentYear, description, phone, website, email, address, logo, documents
 */
router.post(
  "/school",
  authenticate,
  requireEmailVerification,
  uploadRateLimit,
  uploadSchoolFiles,
  (req: Request, res: Response) =>
    profileController.createSchoolProfile(req, res),
);

/**
 * @route   POST /api/v1/profile/student
 * @desc    Create student profile
 * @access  Private (requires authentication and email verification)
 * @body    FormData with fullName, dateOfBirth, gender, phone, address, schoolId, academicLevel, academicYear, majorSubjectsIds, minorSubjects, guardians, profileImage, documents
 */
router.post(
  "/student",
  authenticate,
  requireEmailVerification,
  uploadRateLimit,
  uploadStudentFiles,
  (req: Request, res: Response) =>
    profileController.createStudentProfile(req, res),
);

/**
 * @route   POST /api/v1/profile/teacher
 * @desc    Create teacher profile
 * @access  Private (requires authentication and email verification)
 * @body    FormData with fullName, dateOfBirth, gender, phone, address, schoolId, bio, subjects, education, experience, emergencyContact, profileImage, documents
 */
router.post(
  "/teacher",
  authenticate,
  requireEmailVerification,
  uploadRateLimit,
  uploadTeacherFiles,
  (req: Request, res: Response) =>
    profileController.createTeacherProfile(req, res),
);

/**
 * @route   GET /api/v1/profile/me
 * @desc    Get current user's profile
 * @access  Private (requires authentication)
 */
router.get(
  "/me",
  authenticate,
  generalRateLimit,
  (req: Request, res: Response) => profileController.getMyProfile(req, res),
);

/**
 * @route   GET /api/v1/profile/status
 * @desc    Check profile creation status (for school users)
 * @access  Private (requires authentication)
 */
router.get(
  "/status",
  authenticate,
  generalRateLimit,
  (req: Request, res: Response) =>
    profileController.checkProfileStatus(req, res),
);

/**
 * @route   GET /api/v1/profile/personal
 * @desc    Get personal profile (for school users)
 * @access  Private (requires authentication and email verification)
 */
router.get(
  "/personal",
  authenticate,
  requireEmailVerification,
  generalRateLimit,
  (req: Request, res: Response) =>
    profileController.getPersonalProfile(req, res),
);

/**
 * @route   PUT /api/v1/profile/personal
 * @desc    Update personal profile (for school users)
 * @access  Private (requires authentication and email verification)
 * @body    FormData with fullName, gender, dateOfBirth, phone, address, bio, profileImage
 */
router.put(
  "/personal",
  authenticate,
  requireEmailVerification,
  uploadRateLimit,
  uploadPersonalFiles,
  (req: Request, res: Response) =>
    profileController.updatePersonalProfile(req, res),
);

/**
 * @route   GET /api/v1/profile/school
 * @desc    Get school profile
 * @access  Private (requires authentication and email verification)
 */
router.get(
  "/school",
  authenticate,
  requireEmailVerification,
  generalRateLimit,
  (req: Request, res: Response) => profileController.getSchoolProfile(req, res),
);

/**
 * @route   PUT /api/v1/profile/school
 * @desc    Update school profile
 * @access  Private (requires authentication and email verification)
 * @body    FormData with schoolName, establishmentYear, description, phone, website, email, address, logo, documents, removeDocuments
 * @note    removeDocuments: JSON array of document filenames to remove e.g. ["file1.pdf", "file2.pdf"]
 */
router.put(
  "/school",
  authenticate,
  requireEmailVerification,
  uploadRateLimit,
  uploadSchoolFiles,
  (req: Request, res: Response) =>
    profileController.updateSchoolProfile(req, res),
);

/**
 * @route   PUT /api/v1/profile/teacher
 * @desc    Update teacher profile
 * @access  Private (requires authentication and email verification)
 * @body    FormData with fullName, dateOfBirth, gender, phone, address, bio, subjects, education, experience, emergencyContact, profileImage, documents, documentTypes
 * @note    documentTypes: JSON array of document types corresponding to uploaded documents
 */
router.put(
  "/teacher",
  authenticate,
  requireEmailVerification,
  uploadRateLimit,
  uploadTeacherFiles,
  (req: Request, res: Response) =>
    profileController.updateTeacherProfile(req, res),
);

/**
 * @route   PUT /api/v1/profile/student
 * @desc    Update student profile
 * @access  Private (requires authentication and email verification)
 * @body    FormData with fullName, dateOfBirth, gender, phone, address, academicLevel, academicYear, majorSubjectsIds, minorSubjects, guardians, profileImage, documents
 */
router.put(
  "/student",
  authenticate,
  requireEmailVerification,
  uploadRateLimit,
  uploadStudentFiles,
  (req: Request, res: Response) =>
    profileController.updateStudentProfile(req, res),
);

/**
 * @route   PUT /api/v1/profile/admin
 * @desc    Update admin profile
 * @access  Private (requires authentication and email verification)
 * @body    FormData with fullName, phone, address, bio, profileImage
 */
router.put(
  "/admin",
  authenticate,
  requireEmailVerification,
  uploadRateLimit,
  uploadPersonalFiles, // Reuse personal files upload for admin
  (req: Request, res: Response) =>
    profileController.updateAdminProfile(req, res),
);

export default router;
